/**
 * PublicDownload — customer-facing PDF landing page.
 *
 * Mounted at /d/:kind/:id (kind ∈ agreement | ledger | receipt).
 * Fetches the PDF from the backend using the shop ID passed via ?s=
 * (the existing shop-token mechanism on each PDF endpoint), then triggers
 * a download via blob URL. The customer never sees the backend hostname.
 *
 * No auth required (the shop param + per-PDF ownership check on the
 * backend already gates access). Works from WhatsApp / Email opens.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, FileDown, AlertTriangle, ShieldCheck } from "lucide-react";

const KIND_CONFIG = {
  agreement: {
    label: "Agreement",
    backendPath: (id, shop) => `/api/agreements/${encodeURIComponent(id)}/pdf?shop=${encodeURIComponent(shop)}`,
    filename: (id) => `Agreement-${id}.pdf`,
  },
  ledger: {
    label: "Customer Ledger",
    backendPath: (cid, shop) => `/api/customers/${encodeURIComponent(cid)}/ledger.pdf?shop=${encodeURIComponent(shop)}`,
    filename: (cid) => `Ledger-${cid}.pdf`,
  },
  receipt: {
    label: "Payment Receipt",
    backendPath: (pid, shop) => `/api/payments/${encodeURIComponent(pid)}/receipt.pdf?shop=${encodeURIComponent(shop)}`,
    filename: (pid) => `Receipt-${pid}.pdf`,
  },
};

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function PublicDownload() {
  const { kind, id } = useParams();
  const [search] = useSearchParams();
  const shop = search.get("s");
  const [state, setState] = useState({ status: "loading", error: "", blobUrl: "" });
  const triggeredRef = useRef(false);
  const cfg = KIND_CONFIG[kind];

  useEffect(() => {
    let active = true;
    let blobUrl = "";
    if (!cfg) {
      const t = setTimeout(() => active && setState({ status: "error", error: "Invalid link", blobUrl: "" }), 0);
      return () => { active = false; clearTimeout(t); };
    }
    if (!id || !shop) {
      const t = setTimeout(() => active && setState({ status: "error", error: "Missing parameters", blobUrl: "" }), 0);
      return () => { active = false; clearTimeout(t); };
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND}${cfg.backendPath(id, shop)}`, {
          method: "GET",
          credentials: "omit",
          headers: { Accept: "application/pdf" },
        });
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!active) return;
        setState({ status: "ready", error: "", blobUrl });
        // Auto-trigger download once
        if (!triggeredRef.current) {
          triggeredRef.current = true;
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = cfg.filename(id);
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } catch (err) {
        if (active) setState({ status: "error", error: err.message || "Download failed", blobUrl: "" });
      }
    })();
    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [kind, id, shop, cfg]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="public-download">
      <div className="w-full max-w-md bg-white rounded-md shadow-sm border border-slate-200 px-6 py-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-md overflow-hidden mb-3 flex items-center justify-center">
          <img src="/brand/logo.svg" alt="HisaabPro" width="48" height="48" />
        </div>
        <div className="font-display font-bold text-2xl text-slate-900">HisaabPro</div>
        <div className="text-xs text-slate-500 mb-6">Hisaab strong, recovery strong</div>

        {state.status === "loading" && (
          <div className="space-y-2" data-testid="dl-state-loading">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto" />
            <div className="text-sm text-slate-600">
              {cfg?.label || "Document"} taiyar ho raha hai…
            </div>
          </div>
        )}

        {state.status === "ready" && (
          <div className="space-y-3" data-testid="dl-state-ready">
            <FileDown className="w-7 h-7 text-emerald-600 mx-auto" />
            <div className="font-semibold text-slate-800">Download shuru ho gayi</div>
            <p className="text-xs text-slate-500">
              Agar phone par download nahi shuru hui, neeche button par tap karein.
            </p>
            <a
              href={state.blobUrl}
              download={cfg.filename(id)}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-semibold"
              data-testid="dl-manual-btn"
            >
              <FileDown className="w-4 h-4" /> Download {cfg.label}
            </a>
          </div>
        )}

        {state.status === "error" && (
          <div className="space-y-2" data-testid="dl-state-error">
            <AlertTriangle className="w-7 h-7 text-red-600 mx-auto" />
            <div className="font-semibold text-red-700">Download nahi ho saka</div>
            <p className="text-xs text-slate-500">{state.error}</p>
            <p className="text-[11px] text-slate-400">
              Apne shop owner se naya link mangwa lein.
            </p>
          </div>
        )}

        <div className="text-[10px] text-slate-400 mt-6 pt-4 border-t border-slate-100">
          © HisaabPro — myhisaabpro.com
        </div>
      </div>
    </div>
  );
}
